import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { AbstractEntitiesTableComponent } from '@abstracts/entities/entities-table';
import { ChargingStationsService } from '@services/entity/charging-stations.service';
import { ChargingStationDto } from '@dto/chargingStationDto';
import { ToastService } from '@services/toast/toast.service';
import { TableColumn } from '@models/tableColumn';
import { TableOptions } from '@models/tableOptions';
import { TechnicalChargingStationActionsModalComponent } from './technical-charging-station-actions-modal/technical-charging-station-actions-modal.component';
import { TechnicalChargingStationUnlockModalComponent } from './technical-charging-station-unlock-modal/technical-charging-station-unlock-modal.component';
import { ChargingPointStatusEnum } from "@enums/chargingPointStatus.enum";
import { ChargingStationTableDto } from '../../../dto/chargingStationTableDto';
import { plainToClass } from "class-transformer";


@Component({
  selector: 'app-technical-charging-stations',
  templateUrl: './technical-charging-stations.component.html',
  styleUrls: ['./technical-charging-stations.component.scss'],
})
export class TechnicalChargingStationsComponent extends AbstractEntitiesTableComponent implements OnInit, OnDestroy {
  className = 'TechnicalChargingStationsComponent';
  private statusUpdateSubscription: Subscription;
  public entities: ChargingStationTableDto[];


  tableColumns: TableColumn[] = [
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn2:Name/Bezeichner`,
      property: 'name',
    },
    {
      columnDescription: $localize`:@@TechnicalChargingStationsStatus:Status`,
      property: 'displayStatus',
      options: { useStatusBadgeComponent: true},
    },
    {
      columnDescription: $localize`:@@TechnicalChargingStationsConnectors:Konnektoren`,
      property: 'connectorCount',
      options: { },
    },
  ];

  tableOptions: TableOptions = {
    hideActions: false,
    hideSearch: true,
    rowRouterLink: undefined,
    hideModelPicture: true,
  };

  private sortParam: number;


  constructor(
    public chargingStationsService: ChargingStationsService,
    private toastService: ToastService,
    private modalService: NgbModal,
    injector: Injector
  ) {
    super(chargingStationsService, injector);
  }

  openActionsModal(station: ChargingStationDto): void {
    const ref = this.modalService.open(TechnicalChargingStationActionsModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'xs',
    });
    ref.componentInstance.station = station;
  }

  openUnlockModal(station: ChargingStationDto): void {
    const ref = this.modalService.open(TechnicalChargingStationUnlockModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'xs',
    });
    ref.componentInstance.station = station;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.sortParam = params.sort;
    });
    super.ngOnInit();

    if (!this.sortParam) {
      this.tableFunctions.sortColumn = "name";
      this.tableFunctions.sortDirection = "asc";
    }
  }

  initializeEntities(): void {
    this.chargingStationsService.getChargingStationsOfCurrentCustomer()
      .subscribe(
        (result) => {
          this.entities = plainToClass(ChargingStationTableDto, result);
          this.sortEntities();
          this.mapChargeMode();
          this.ConnectorCount();
          super.setupTable();
        },
        (error) => { }
      );
  }

  sortEntities() {
    // pre-sort by status column if required
    if (this.sortParam) {
      // all that have sortParam in their status column:
      var entitiesWithSortParam = this.entities.filter(
        (x) =>
          x.statusId == this.sortParam ||
          x.chargingPoints.some((cp) => cp.statusId == this.sortParam)
      );
      // all others:
      var entitiesWithoutSortParam = this.entities.filter(
        (x) => !entitiesWithSortParam.includes(x)
      );
      this.entities = entitiesWithSortParam.concat(entitiesWithoutSortParam);
    }

    // create searchable string for the status column
    this.entities.forEach((e) => {
      // The charging station status badge shows each CP status as long as the CS is available and the CS status otherwise.
      if (e.status.id == ChargingPointStatusEnum.Available) {
        e.chargingPointsStatus = e.chargingPoints
          .map((cp) => cp.status.name)
          .join();
      } else {
        e.chargingPointsStatus = e.status.name;
      }
    });
  }

  mapChargeMode() {
    this.entities.forEach((f) => {
      let chargeMode = "";
      if (f.isFreeCharge) {
        chargeMode = `<span class="text-success">Kostenloses Laden</span>`;
      }
      if (f.isPrivateCharge) {
        chargeMode = `<span class="text-dark">Autorisiertes Laden</span>`;
      }
      if (f.isPublicCharge) {
        chargeMode = `<span class="text-orange">Autorisiertes &amp; QR-Code-Laden</span>`;
      }
      f.displayChargeMode = chargeMode;
    });
  }

  ConnectorCount() {
    this.entities.forEach((f) => {
      let count = 0;
      count = f.chargingPoints.length
      f.connectorCount = count;
    })
  }

  onSort() {
    if (this.sortParam) {
      this.sortParam = undefined;
      this.router.navigate([], {
        queryParams: {
          sort: null,
        },
        queryParamsHandling: "merge",
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.statusUpdateSubscription) {
      this.statusUpdateSubscription.unsubscribe();
    }
  }

  matches(entity: ChargingStationDto, term: string): boolean {
    if (!term?.trim()) return true;
    const t = term.toLowerCase();
    return (
      entity.name?.toLowerCase().includes(t) ||
      entity.displayStatus?.toLowerCase().includes(t) ||
      entity.status?.name?.toLowerCase().includes(t)
    );
  }

  connectorCount(station: ChargingStationDto): number {
    return station?.chargingPoints?.length ?? 0;
  }
}
